package org.catharsis.spendtel.feature.presentation

import androidx.lifecycle.viewModelScope
import io.github.aakira.napier.Napier
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import org.catharsis.spendtel.core.api.AnalyticsApi
import org.catharsis.spendtel.core.api.AuthenticationRepo
import org.catharsis.spendtel.core.api.PreferencesManager
import org.catharsis.spendtel.core.api.SpendTelRepo
import org.catharsis.spendtel.core.arch.MviViewModel
import org.catharsis.spendtel.core.arch.ViewEffect
import org.catharsis.spendtel.core.arch.ViewEvent
import org.catharsis.spendtel.core.arch.ViewState
import org.catharsis.spendtel.feature.data.EventName
import org.catharsis.spendtel.feature.data.ScreenName
import org.catharsis.spendtel.feature.data.SpendtelUser
import kotlin.toString

class SpendTelViewModel(
    private val spendTelRepo: SpendTelRepo,
    private val authenticationRepo: AuthenticationRepo,
    private val preferencesManager: PreferencesManager,
    private val analytics: AnalyticsApi,
): MviViewModel<SpendTelEvent, SpendTelState, SpendTelEffect>(
    initialState = SpendTelState()
) {
    private val _spendTelStateFlow: MutableStateFlow<SpendTelState> = MutableStateFlow(value = SpendTelState())
    val spendTelState: StateFlow<SpendTelState> = _spendTelStateFlow.asStateFlow()
    
    val hasViewedHowItWorksScreen: Flow<Boolean> = preferencesManager.hasViewedHowItWorksScreen
    
    fun markHowItWorksAsViewed() {
        viewModelScope.launch {
            preferencesManager.setHasViewedHowItWorksScreen(true)
        }
    }

    init {
        toggleLoadingState(loading = true)

        viewModelScope.launch {
            getMyUser()
            initData()
            delay(2000) // show loading for at least 2 sec
            toggleLoadingState(loading = false)
        }
    }

    private fun initData() {
        if (!_spendTelStateFlow.value.dataInitComplete) {
            fetchTotalUserCount()

            viewModelScope.launch {
                _spendTelStateFlow.emit(
                    _spendTelStateFlow.value.copy(
                        dataInitComplete = true
                    )
                )
            }
        }
    }

    override fun receiveEvent(event: SpendTelEvent) {
    }

    fun listenToUserChanges(uid: String): Flow<SpendtelUser?> = spendTelRepo.listenToUserChanges(uid)

    fun updateUserFCMToken(token: String) {
        viewModelScope.launch {
            if (authenticationRepo.isAuthenticated) {
                authenticationRepo.getCurrentUser()?.let { user ->
                    spendTelRepo.updateUserFCMToken(user.uid, token)
                }
            }
        }
    }

    fun logScreenViewed(screenName: ScreenName) {
        analytics.logScreenViewed(screenName)
    }

    fun logClickEvent(eventName: EventName, extras: Map<String, Any>? = null) {
        analytics.logClickEvent(eventName, extras)
    }

    suspend fun getUserById(uid: String): SpendtelUser? {
        return spendTelRepo.getUserById(uid)
    }

    private suspend fun getMyUser() {
        authenticationRepo.getCurrentUser()?.let {

            _spendTelStateFlow.emit(_spendTelStateFlow.value.copy(
                currentUser =  it,
                currentUserId = it.uid,
                profilePic = it.profileImage,
            ))
        }

    }

    /*
    * Deletes the user given to the function, or the current user if no user is directly targeted
    * */
    fun deleteUser(userId: String? = null) {
        viewModelScope.launch {
            if (userId == null) {
                authenticationRepo.getCurrentUser()?.let {
                    spendTelRepo.deleteUser(it.uid)
                }
            } else {
                spendTelRepo.deleteUser(userId)
            }
        }
    }

    private fun fetchTotalUserCount() {
        viewModelScope.launch {
            val result = spendTelRepo.getTotalNumberOfUsers()
                .onFailure {
                    emitEffect {
                        SpendTelEffect.OnError(it.message ?: "An error occurred fetching content cards")
                    }
                }

            if (result.isSuccess) {
                _spendTelStateFlow.emit(_spendTelStateFlow.value.copy(
                    totalUserCount = result.getOrNull() ?: 0,
                ))
            }
        }
    }

    // Updates the users username in the DB
    fun updateUsername(userId: String, newUsername: String) {
        viewModelScope.launch { spendTelRepo.updateUsername(userId, newUsername) }
    }

    // Toggles the users general notifications setting in the DB
    fun updateUserGeneralNotificationSetting(userId: String, enable: Boolean) {
        viewModelScope.launch { spendTelRepo.updateUserGeneralNotificationsSetting(userId, enable) }
    }

    // Toggle the users dark mode setting in the DB
    fun updateUserDarkModeActiveSettings(userId: String, darkModeActive: Boolean) {
        viewModelScope.launch { spendTelRepo.updateUserDarkModeSetting(userId, darkModeActive) }
    }

    fun updateUserEULAAgreement() {
        viewModelScope.launch {
            authenticationRepo.getCurrentUser()?.let {
                spendTelRepo.updateUserEULAAgreement(it.uid, true)
            }
        }
    }

    private fun toggleLoadingState(loading: Boolean) {
        viewModelScope.launch {
            _spendTelStateFlow.emit(_spendTelStateFlow.value.copy(isLoading = loading))
        }
    }
}

data class SpendTelState (
    val currentUser: SpendtelUser? = null,
    val profilePic: String? = null,
    val currentUserId: String? = null,
    val totalUserCount: Int = 0,
    val isLoading: Boolean = false,
    val dataInitComplete: Boolean = false,
): ViewState

sealed class SpendTelEvent: ViewEvent

sealed class SpendTelEffect: ViewEffect {
    data class OnError(val message: String) : SpendTelEffect()
}